import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase.js';
import { routes } from '../../routes.js';
import { Button, FormField, TextInput } from '../../components/ui/index.js';
import { ScopeWordmark } from '../../components/ScopeWordmark.js';

interface ForgotPasswordProps {
  variant?: 'agency' | 'portal';
}

const forgotSchema = z.object({
  email: z.string().email('Enter a valid email'),
});
type ForgotValues = z.infer<typeof forgotSchema>;

/**
 * Forgot-password screen for both agency users and client contacts.
 * Calls Supabase resetPasswordForEmail; the link in the email opens
 * /reset-password (or /portal/reset-password) to set a new password.
 */
export function ForgotPassword({ variant = 'agency' }: ForgotPasswordProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotValues>({ resolver: zodResolver(forgotSchema) });

  const resetPath = variant === 'portal' ? routes.portalResetPassword : routes.resetPassword;
  const loginPath = variant === 'portal' ? routes.portalLogin : routes.login;

  const onSubmit = async (values: ForgotValues) => {
    setSubmitError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
      redirectTo: window.location.origin + resetPath,
    });
    if (error) {
      setSubmitError(error.message);
      return;
    }
    setSubmitted(true);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 font-sans">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <ScopeWordmark />
        </div>
        <div className="rounded-lg border border-scope-border bg-white p-8 shadow-scope">
          <h1 className="mb-1 text-2xl font-semibold tracking-tight text-scope-navy">
            Reset your password
          </h1>
          <p className="mb-6 text-sm text-scope-text-muted">
            We&apos;ll email you a link to set a new password.
          </p>

          {submitted ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-status-green bg-status-green-bg px-3 py-3 text-sm text-status-green">
                Check your inbox for the reset link.
              </div>
              <Link
                to={loginPath}
                className="block text-center text-sm text-scope-text-muted hover:text-scope-navy"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <FormField label="Email" htmlFor="email" error={errors.email?.message}>
                <TextInput
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@agency.com"
                  {...register('email')}
                />
              </FormField>

              {submitError && (
                <div className="rounded-lg border border-status-red bg-status-red-bg px-3 py-2 text-xs text-status-red">
                  {submitError}
                </div>
              )}

              <Button type="submit" className="w-full" loading={isSubmitting}>
                Send reset link
              </Button>

              <Link
                to={loginPath}
                className="block text-center text-sm text-scope-text-muted hover:text-scope-navy"
              >
                Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
